
import React, { useCallback, useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "react-datepicker/dist/react-datepicker.css";

import "../../../../style/custommillingHubdashboard.css";
import "../../../../style/customresponsivefile.css";
import "../../../../style/Cancelorder.css";

import { apiAxios } from "../../../../utils/apiAxios";
import SearchBox from "../../../../Interfaces/SearchBoxProps";
import AdminDeletedFileHistory from "../components/AdminDeletedFileHistory";

import {
  ManagedFile,
  AdminFileManagementRow,
} from "../Interfaces/AdminFileManagementRow";

const AdminFileManagement: React.FC = () => {
  const [rows, setRows] = useState<AdminFileManagementRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState<"files" | "history">("files");

  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [totalFiles, setTotalFiles] = useState(0);
  const [totalEligibleFiles, setTotalEligibleFiles] = useState(0);
  const [totalEligibleCases, setTotalEligibleCases] = useState(0);
  const [totalCompletedCases, setTotalCompletedCases] = useState(0);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);

  const [selectedDeleteRow, setSelectedDeleteRow] =
    useState<AdminFileManagementRow | null>(null);

  const [deletingCaseId, setDeletingCaseId] = useState<number | null>(null);
  const [deletingAll, setDeletingAll] = useState(false);

  // FETCH FILE MANAGEMENT DATA
  const fetchFiles = useCallback(
    async (
      _pageNumber = 1,
      search = "",
      startDate = "",
      endDate = ""
    ) => {
      try {
        setLoading(true);

        const response = await apiAxios.get("/Central/file-management", {
          params: {
            search,
            fromDate: startDate || undefined,
            toDate: endDate || undefined,
          },
        });

        const json = response.data;

        if (!json?.isSuccess) {
          toast.error(
            json?.status ||
              json?.message ||
              "Failed to load file management data"
          );

          setRows([]);
          setTotalRows(0);
          return;
        }

        const result = json.dataOne ?? json.data ?? [];
        const items = Array.isArray(result) ? result : result?.data ?? [];

        const mappedRows: AdminFileManagementRow[] = items.map(
          (item: any) => ({
            caseId: Number(
              item.caseID ?? item.caseId ?? item.CaseID ?? 0
            ),

            completionDate:
              item.completionDate ??
              item.CompletionDate ??
              item.completedDate ??
              item.CompletedDate ??
              null,

            orderId: String(
              item.orderID ?? item.orderId ?? item.OrderID ?? ""
            ),

            clinicName: String(
              item.clinic ??
                item.Clinic ??
                item.clinicName ??
                item.ClinicName ??
                ""
            ),

            orderStatus: String(
              item.orderStatus ??
                item.OrderStatus ??
                item.currentOrderState ??
                item.CurrentOrderState ??
                ""
            ),

            dentistFiles: Number(
              item.dentistFiles ?? item.DentistFiles ?? 0
            ),

            designerFiles: Number(
              item.designerFiles ?? item.DesignerFiles ?? 0
            ),

            totalFileCount: Number(
              item.totalFiles ??
                item.TotalFiles ??
                item.totalFileCount ??
                item.TotalFileCount ??
                0
            ),

            isEligibleForDelete:
              item.isEligibleForDelete ??
              item.IsEligibleForDelete ??
              false,
          })
        );

        setRows(mappedRows);
        setTotalRows(mappedRows.length);

        // Automatically calculate the completion date range.
        // Only apply the default when no dates are selected.
        if (!startDate && !endDate) {
          const completionDates = mappedRows
            .filter(
              (row) => row.isEligibleForDelete && row.completionDate
            )
            .map((row) => {
              const date = String(row.completionDate).slice(0, 10);

              return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : "";
            })
            .filter((date) => date !== "")
            .sort();

          if (completionDates.length > 0) {
            setFromDate(completionDates[0]);
            setToDate(completionDates[completionDates.length - 1]);
          }
        }
      } catch (error: any) {
        console.error("Error fetching file management data:", error);

        toast.error(
          error?.response?.data?.status ||
            error?.response?.data?.message ||
            "Failed to load file management data"
        );

        setRows([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // FETCH SUMMARY CARDS
  const fetchFileManagementCards = useCallback(async () => {
    try {
      const response = await apiAxios.get(
        "/Central/file-management/cards"
      );

      const json = response.data;

      if (json?.isSuccess) {
        const result = json.dataOne ?? json.data ?? {};

        setTotalFiles(
          Number(
            result.totalActiveFiles ??
              result.TotalActiveFiles ??
              0
          )
        );

        setTotalCompletedCases(
          Number(
            result.totalCompletedCases ??
              result.TotalCompletedCases ??
              0
          )
        );

        setTotalEligibleFiles(
          Number(
            result.totalEligibleFiles ??
              result.TotalEligibleFiles ??
              0
          )
        );

        setTotalEligibleCases(
          Number(
            result.totalEligibleCases ??
              result.TotalEligibleCases ??
              0
          )
        );
      }
    } catch (error: any) {
      console.error("Error fetching file management cards:", error);

      toast.error(
        error?.response?.data?.status ||
          error?.response?.data?.message ||
          "Failed to load file management cards"
      );
    }
  }, []);

  // INITIAL LOAD
  useEffect(() => {
    void fetchFileManagementCards();
  }, [fetchFileManagementCards]);

  // SEARCH + DATE FILTERS
  useEffect(() => {
    if (fromDate && toDate && fromDate > toDate) {
      return;
    }

    const timer = setTimeout(
      () => {
        void fetchFiles(page, searchText.trim(), fromDate, toDate);
      },
      searchText.trim() ? 400 : 0
    );

    return () => clearTimeout(timer);
  }, [page, searchText, fromDate, toDate, fetchFiles]);

  // SAFE FILE URL
  const constructFileUrl = (filePath: string): string => {
    if (!filePath) return "";

    const cleanPath = filePath.trim();

    if (
      cleanPath.startsWith("https://") ||
      cleanPath.startsWith("http://")
    ) {
      return cleanPath;
    }

    const baseUrl = (
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_API_URL ||
      ""
    )
      .replace(/\/api\/?$/, "")
      .replace(/\/$/, "");

    const cleanFilePath = cleanPath.replace(/^\/+/, "");

    return `${baseUrl}/${cleanFilePath}`;
  };

  // DOWNLOAD FILE
  const downloadFile = async (file: ManagedFile) => {
    if (!file.imagePath) {
      toast.error("File path is not available");
      return;
    }

    const fullUrl = constructFileUrl(file.imagePath);

    try {
      const token = sessionStorage.getItem("token");

      const response = await apiAxios.get(fullUrl, {
        responseType: "blob",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          AuthorizationData: token || "",
        },
      });

      const contentType: string =
        typeof response.headers["content-type"] === "string"
          ? response.headers["content-type"]
          : "application/octet-stream";

      const blob = new Blob([response.data], {
        type: contentType,
      });

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = file.fileName || "download";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(objectUrl);

      toast.success(`Downloading ${file.fileName}`);
    } catch (error: any) {
      console.error("File download failed:", error);

      toast.error(
        error?.response?.data?.message ||
          "Failed to download file"
      );
    }
  };

  // DELETE FILES FOR ONE CASE
  const handleDelete = async () => {
    if (!selectedDeleteRow) return;

    const caseId = selectedDeleteRow.caseId;

    try {
      setDeletingCaseId(caseId);

      const { data } = await apiAxios.delete(
        `/Central/file-management/cases/${caseId}`
      );

      if (data?.isSuccess) {
        toast.success(
          data?.status || "Case files deleted successfully."
        );

        setShowDeleteModal(false);
        setSelectedDeleteRow(null);

        await fetchFiles(page, searchText.trim(), fromDate, toDate);
        await fetchFileManagementCards();
      } else {
        toast.error(
          data?.status || "Failed to delete case files."
        );
      }
    } catch (error: any) {
      console.error(
        "Delete case files error:",
        error?.response?.data || error
      );

      toast.error(
        error?.response?.data?.status ||
          error?.response?.data?.message ||
          "Something went wrong while deleting."
      );
    } finally {
      setDeletingCaseId(null);
    }
  };

  // DELETE ALL ELIGIBLE FILES
  const handleDeleteAllEligible = async () => {
    if (!fromDate || !toDate) {
      toast.warning("Please select both From Date and To Date.");
      return;
    }

    if (fromDate > toDate) {
      toast.warning("From Date cannot be later than To Date.");
      return;
    }

    try {
      setDeletingAll(true);

      const response = await apiAxios.delete(
        "/Central/file-management/delete-all-eligible",
        {
          params: {
            fromDate,
            toDate,
          },
        }
      );

      const result = response.data;

      if (result?.isSuccess !== true) {
        toast.error(
          result?.status ||
            result?.message ||
            "Failed to delete eligible files."
        );
        return;
      }

      toast.success(
        result?.status ||
          result?.message ||
          "Eligible files deleted successfully."
      );

      setShowDeleteAllModal(false);

      await fetchFiles(1, searchText.trim(), fromDate, toDate);
      await fetchFileManagementCards();

      setPage(1);
    } catch (error: any) {
      console.error(
        "Delete All API error:",
        error?.response?.data || error
      );

      toast.error(
        error?.response?.data?.status ||
          error?.response?.data?.message ||
          "Failed to delete eligible files."
      );
    } finally {
      setDeletingAll(false);
    }
  };

  // OPEN DELETE CONFIRMATION
  const confirmDelete = (row: AdminFileManagementRow) => {
    if (!row.isEligibleForDelete) {
      toast.warning(
        "Only completed and paid cases can be deleted."
      );
      return;
    }

    setSelectedDeleteRow(row);
    setShowDeleteModal(true);
  };

  // RESET FILTERS
  const resetFilters = () => {
    setFromDate("");
    setToDate("");
    setSearchText("");
    setPage(1);
  };

  // DATE HELPERS
  const parseDate = (value: string): Date | null => {
    if (!value) return null;

    const date = new Date(`${value}T00:00:00`);

    return Number.isNaN(date.getTime()) ? null : date;
  };

  const formatDisplayDate = (
    value: string | Date | null | undefined
  ): string => {
    if (!value) return "-";

    if (typeof value === "string") {
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

      if (match) {
        const [, year, month, day] = match;
        const date = new Date(
          Number(year),
          Number(month) - 1,
          Number(day)
        );

        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return "";

    return `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  // TABLE COLUMNS
  const columns: TableColumn<AdminFileManagementRow>[] = [
    {
      name: "Order ID",
      selector: (row) => row.orderId || "-",
      sortable: true,
      minWidth: "120px",
    },
    {
      name: "Case ID",
      selector: (row) => row.caseId,
      sortable: true,
      minWidth: "95px",
    },
    {
      name: "Completed On",
      selector: (row) => row.completionDate ?? "",
      cell: (row) => formatDisplayDate(row.completionDate),
      sortable: true,
      width: "150px",
    },
    {
      name: "Clinic",
      selector: (row) => row.clinicName || "-",
      sortable: true,
      wrap: true,
      minWidth: "150px",
    },
    {
      name: "Dentist Files",
      selector: (row) => row.dentistFiles,
      sortable: true,
      center: true,
      cell: (row) => (
        <span className="badge bg-primary">
          {row.dentistFiles}
        </span>
      ),
      minWidth: "115px",
    },
    {
      name: "Designer Files",
      selector: (row) => row.designerFiles,
      sortable: true,
      center: true,
      cell: (row) => (
        <span className="badge bg-info text-dark">
          {row.designerFiles}
        </span>
      ),
      minWidth: "125px",
    },
    {
      name: "Total Files",
      selector: (row) => row.totalFileCount,
      sortable: true,
      center: true,
      minWidth: "100px",
    },
    {
      name: "Order Status",
      cell: (row) => (
        <span
          className={`badge ${
            row.orderStatus.toUpperCase() === "COMPLETED"
              ? "bg-success"
              : "bg-secondary"
          }`}
        >
          {row.orderStatus || "-"}
        </span>
      ),
      sortable: true,
      minWidth: "145px",
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex justify-content-center gap-2">
          <button
            type="button"
            className="action-btn delete custom-edit-icon border-0 bg-transparent"
            onClick={() => confirmDelete(row)}
            title={
              row.isEligibleForDelete
                ? "Delete files"
                : "Only completed and paid cases can be deleted"
            }
            style={{
              cursor: row.isEligibleForDelete
                ? "pointer"
                : "not-allowed",
              opacity: row.isEligibleForDelete ? 1 : 0.5,
            }}
          >
            <i className="bi bi-trash" />
          </button>
        </div>
      ),
      width: "120px",
      center: true,
    },
  ];

  return (
    <div className="container-fluid admin-file-management-page">
      <style>{`
        .admin-file-management-page {
          padding: 22px 24px 30px;
          color: #253247;
          min-height: calc(100vh - 70px);
        }

        .afm-page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 18px;
          flex-wrap: wrap;
          margin-bottom: 22px;
        }

        .afm-page-title {
          color: #253247;
          font-size: 23px;
          font-weight: 700;
          margin: 0 0 5px;
          letter-spacing: -.3px;
        }

        .afm-page-subtitle {
          color: #78869a;
          font-size: 13px;
          margin: 0;
        }

        .afm-delete-all {
          border: 0;
          border-radius: 7px;
          padding: 10px 15px;
          font-size: 12px;
          font-weight: 600;
          box-shadow: 0 3px 8px rgba(220, 53, 69, .14);
          white-space: nowrap;
        }

        .afm-summary-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(220px, 1fr));
          gap: 15px;
          max-width: 700px;
          margin-bottom: 22px;
        }

        .afm-summary-card {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          min-height: 86px;
          padding: 17px 19px;
          background: #fff;
          border: 1px solid #e8edf4;
          border-radius: 10px;
          box-shadow: 0 3px 10px rgba(32, 54, 86, .045);
        }

        .afm-summary-label {
          color: #7b8798;
          font-size: 12px;
          margin: 0 0 7px;
        }

        .afm-summary-value {
          color: #243248;
          font-size: 25px;
          line-height: 1;
          font-weight: 700;
          margin: 0;
        }

        .afm-summary-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 43px;
          height: 43px;
          border-radius: 10px;
          background: #eef5ff;
          color: #2673ee;
          font-size: 21px;
          flex-shrink: 0;
        }

        .afm-summary-icon.amber {
          background: #fff6e8;
          color: #f2a22a;
        }

        .afm-table-card {
          background: #fff;
          border: 1px solid #e8edf4;
          border-radius: 11px;
          padding: 20px;
          box-shadow: 0 4px 14px rgba(32, 54, 86, .045);
        }

        .afm-table-heading {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 18px;
        }

        .afm-section-title {
          color: #27364b;
          font-size: 16px;
          font-weight: 700;
          margin: 0 0 5px;
        }

        .afm-section-note {
          color: #8994a4;
          font-size: 12px;
          margin: 0;
        }

        .afm-filter-toolbar {
          display: grid;
          grid-template-columns:
            minmax(145px, 170px)
            minmax(145px, 170px)
            minmax(190px, 1fr)
            auto;
          align-items: end;
          gap: 12px;
          padding: 15px;
          margin-bottom: 17px;
          background: #f8faff;
          border: 1px solid #e8edf5;
          border-radius: 9px;
        }

        .afm-filter-field {
          min-width: 0;
        }

        .afm-filter-label {
          display: block;
          color: #657287;
          font-size: 11px;
          font-weight: 600;
          margin: 0 0 6px;
        }

        .manager-datepicker-wrap {
          position: relative;
          width: 100%;
        }

        .manager-datepicker-wrapper {
          display: block !important;
          width: 100%;
        }

        .manager-datepicker-input {
          width: 100% !important;
          height: 38px;
          padding: 8px 34px 8px 11px !important;
          border: 1px solid #dce3ed !important;
          border-radius: 6px !important;
          background: #fff !important;
          color: #344258 !important;
          font-size: 12px !important;
          box-shadow: none !important;
        }

        .manager-datepicker-input:focus {
          border-color: #7eafff !important;
          box-shadow: 0 0 0 3px rgba(38, 115, 238, .10) !important;
          outline: none;
        }

        .manager-datepicker-icon {
          position: absolute;
          top: 50%;
          right: 11px;
          transform: translateY(-50%);
          color: #3979db;
          font-size: 14px;
          pointer-events: none;
          z-index: 2;
        }

        .manager-datepicker-popper {
          z-index: 12000 !important;
        }

        .manager-datepicker-popper .react-datepicker {
          border: 1px solid #e0e7f0;
          border-radius: 9px;
          box-shadow: 0 12px 30px rgba(24, 42, 70, .18);
          overflow: hidden;
        }

        .afm-search-wrap {
          min-width: 0;
        }

        .afm-search-wrap > * {
          max-width: 100%;
        }

        .afm-reset-btn {
          height: 38px;
          padding: 0 13px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
        }

        .afm-validation {
          font-size: 12px;
          border-radius: 7px;
          margin: -3px 0 14px;
        }

        .admin-file-management-page .admin-delivery-table {
          overflow: visible;
        }

        .admin-file-management-page .rdt_Table {
          color: #344258;
          font-size: 12px;
        }

        .admin-file-management-page .rdt_TableHead {
          border-radius: 7px 7px 0 0;
        }

        .admin-file-management-page .rdt_TableHeadRow {
          min-height: 43px;
          background: #f1f5fb;
          border-bottom: 1px solid #e5ebf3;
        }

        .admin-file-management-page .rdt_TableCol {
          color: #5b687c;
          font-size: 11px;
          font-weight: 700;
        }

        .admin-file-management-page .rdt_TableRow {
          min-height: 47px;
          border-bottom: 1px solid #edf1f6;
        }

        .admin-file-management-page .rdt_TableCell {
          padding-top: 9px;
          padding-bottom: 9px;
        }

        .admin-file-management-page .rdt_TableRow:hover {
          background: #f7faff;
        }

        .admin-file-management-page .rdt_Pagination {
          border-top: 1px solid #edf1f6;
          color: #7b8798;
          font-size: 11px;
        }

        .admin-file-management-page .badge {
          font-size: 10px;
          font-weight: 600;
          padding: 5px 8px;
          border-radius: 5px;
        }

        .admin-file-management-page .action-btn {
          color: #dc3545;
          font-size: 14px;
          padding: 6px 9px;
          border-radius: 5px;
        }

        .admin-file-management-page .action-btn:hover {
          background: #fff0f1 !important;
        }

        .custom-top-modal .modal-content {
          border: 0;
          border-radius: 13px;
          overflow: hidden;
          box-shadow: 0 18px 55px rgba(20, 35, 60, .22);
        }

        .custom-top-modal .modal-header {
          padding: 18px 22px;
          border-bottom: 1px solid #edf0f5;
        }

        .custom-top-modal .modal-title {
          font-size: 17px;
          font-weight: 700;
        }

        .custom-top-modal .modal-body {
          padding: 24px 24px 20px;
          color: #344258;
        }

        .custom-top-modal .modal-footer {
          padding: 15px 22px 21px;
          border-top: 0;
          gap: 10px;
        }

        .custom-top-modal .modal-footer > * {
          margin: 0 !important;
          min-width: 88px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
        }

        .afm-delete-detail {
          padding: 13px 15px;
          margin-top: 15px;
          text-align: left;
          border: 1px solid #e8edf5;
          background: #f8faff;
          border-radius: 8px;
          font-size: 12px;
        }

        .afm-delete-detail-row {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 4px 0;
        }

        .afm-delete-detail-row span {
          color: #7c899b;
        }

        .afm-delete-detail-row strong {
          color: #344258;
          overflow-wrap: anywhere;
        }

        @media (max-width: 900px) {
          .afm-filter-toolbar {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .afm-search-wrap {
            grid-column: 1 / -1;
          }

          .afm-reset-btn {
            justify-self: start;
          }
        }

        @media (max-width: 575px) {
          .admin-file-management-page {
            padding: 15px 10px 22px;
          }

          .afm-page-title {
            font-size: 20px;
          }

          .afm-delete-all {
            width: 100%;
          }

          .afm-summary-grid {
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .afm-summary-card {
            min-height: 76px;
          }

          .afm-table-card {
            padding: 13px 10px;
          }

          .afm-filter-toolbar {
            grid-template-columns: 1fr;
            padding: 12px;
          }

          .afm-search-wrap {
            grid-column: auto;
          }

          .afm-reset-btn {
            width: 100%;
          }

          .custom-top-modal .modal-body {
            padding: 20px 17px;
          }
        }
      `}</style>

      {/* PAGE HEADER */}
      <header className="afm-page-header">
        <div>
          <h1 className="afm-page-title">File Management</h1>
          <p className="afm-page-subtitle">
            Manage Dentist and Designer uploaded files.
          </p>
        </div>

        {activeTab === "files" && (
          <button
            type="button"
            className="btn btn-danger afm-delete-all"
            disabled={deletingAll || deletingCaseId !== null}
            onClick={() => setShowDeleteAllModal(true)}
          >
            <i className="bi bi-trash3 me-2" />
            Delete All Eligible Files
          </button>
        )}
      </header>

      {/* TAB NAVIGATION */}
      <div className="d-flex gap-2 mb-4 border-bottom pb-2">
        <button
          type="button"
          className={`btn ${
            activeTab === "files"
              ? "btn-primary"
              : "btn-outline-secondary"
          }`}
          onClick={() => setActiveTab("files")}
        >
          <i className="bi bi-folder2-open me-2" />
          File Management
        </button>

        <button
          type="button"
          className={`btn ${
            activeTab === "history"
              ? "btn-primary"
              : "btn-outline-secondary"
          }`}
          onClick={() => setActiveTab("history")}
        >
          <i className="bi bi-clock-history me-2" />
          Deleted History
        </button>
      </div>

      {/* FILE MANAGEMENT TAB */}
      {activeTab === "files" && (
        <>
          {/* SUMMARY CARDS */}
          <section
            className="afm-summary-grid"
            aria-label="File management summary"
          >
            <div className="afm-summary-card">
              <div>
                <p className="afm-summary-label">Total Active Files</p>
                <h2 className="afm-summary-value">{totalFiles}</h2>
              </div>

              <div className="afm-summary-icon">
                <i className="bi bi-folder2-open" />
              </div>
            </div>

            <div className="afm-summary-card">
              <div>
                <p className="afm-summary-label">Total Completed Cases</p>
                <h2 className="afm-summary-value">
                  {totalCompletedCases}
                </h2>
              </div>

              <div className="afm-summary-icon amber">
                <i className="bi bi-file-earmark-check" />
              </div>
            </div>
          </section>

          {/* UPLOADED FILES */}
          <section className="afm-table-card">
            <div className="afm-table-heading">
              <div>
                <h2 className="afm-section-title">Uploaded Files</h2>
                <p className="afm-section-note">
                  Only completed and paid cases are eligible for deletion.
                </p>
              </div>
            </div>

            {/* FILTER TOOLBAR */}
            <div className="afm-filter-toolbar">
              {/* FROM DATE */}
              <div className="afm-filter-field">
                <label
                  htmlFor="fileManagementFromDate"
                  className="afm-filter-label"
                >
                  From Date
                </label>

                <div className="manager-datepicker-wrap">
                  <DatePicker
                    id="fileManagementFromDate"
                    selected={parseDate(fromDate)}
                    onChange={(date: Date | null) => {
                      setFromDate(formatDate(date));
                      setPage(1);
                    }}
                    selectsStart
                    startDate={parseDate(fromDate)}
                    endDate={parseDate(toDate)}
                    maxDate={parseDate(toDate) ?? undefined}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="DD-MM-YYYY"
                    className="form-control manager-datepicker-input"
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    wrapperClassName="manager-datepicker-wrapper"
                    popperClassName="manager-datepicker-popper"
                    popperPlacement="bottom-start"
                    autoComplete="off"
                  />

                  <i
                    className="bi bi-calendar3 manager-datepicker-icon"
                    aria-hidden="true"
                  />
                </div>
              </div>

              {/* TO DATE */}
              <div className="afm-filter-field">
                <label
                  htmlFor="fileManagementToDate"
                  className="afm-filter-label"
                >
                  To Date
                </label>

                <div className="manager-datepicker-wrap">
                  <DatePicker
                    id="fileManagementToDate"
                    selected={parseDate(toDate)}
                    onChange={(date: Date | null) => {
                      setToDate(formatDate(date));
                      setPage(1);
                    }}
                    selectsEnd
                    startDate={parseDate(fromDate)}
                    endDate={parseDate(toDate)}
                    minDate={parseDate(fromDate) ?? undefined}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="DD-MM-YYYY"
                    className="form-control manager-datepicker-input"
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    wrapperClassName="manager-datepicker-wrapper"
                    popperClassName="manager-datepicker-popper"
                    popperPlacement="bottom-start"
                    autoComplete="off"
                  />

                  <i
                    className="bi bi-calendar3 manager-datepicker-icon"
                    aria-hidden="true"
                  />
                </div>
              </div>

              {/* SEARCH */}
              <div className="afm-filter-field afm-search-wrap">
                <label className="afm-filter-label">
                  Search files
                </label>

                <SearchBox
                  value={searchText}
                  onChange={(value: string) => {
                    setSearchText(value);
                    setPage(1);
                  }}
                />
              </div>

              {/* RESET */}
              <button
                type="button"
                className="btn btn-outline-secondary afm-reset-btn"
                onClick={resetFilters}
                title="Reset date and search filters"
              >
                <i className="bi bi-arrow-counterclockwise me-1" />
                Reset
              </button>
            </div>

            {fromDate && toDate && fromDate > toDate && (
              <div className="alert alert-warning py-2 afm-validation">
                From Date cannot be later than To Date.
              </div>
            )}

            {/* DATA TABLE */}
            <DataTable
              columns={columns}
              data={rows}
              pagination
              paginationPerPage={perPage}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              paginationTotalRows={totalRows}
              onChangePage={(newPage) => setPage(newPage)}
              highlightOnHover
              striped
              responsive
              progressPending={loading}
              fixedHeader
              fixedHeaderScrollHeight="500px"
              persistTableHead
              noDataComponent={
                <div className="p-4 text-muted">
                  No file management records found.
                </div>
              }
              className="admin-delivery-table"
            />
          </section>
        </>
      )}

      {/* DELETED HISTORY COMPONENT */}
      {activeTab === "history" && <AdminDeletedFileHistory />}

      {/* DELETE SINGLE CASE MODAL */}
      <Modal
        show={showDeleteModal}
        onHide={() => {
          if (deletingCaseId === null) {
            setShowDeleteModal(false);
            setSelectedDeleteRow(null);
          }
        }}
        dialogClassName="custom-top-modal afm-confirm-modal"
        centered
      >
        <Modal.Body className="text-center">
          <div
            className="afm-summary-icon mx-auto mb-3"
            style={{
              background: "#fff0f1",
              color: "#dc3545",
            }}
          >
            <i className="bi bi-trash3" />
          </div>

          <h5 className="fw-bold mb-2">Delete case files?</h5>

          <p className="text-muted small mb-0">
            Are you sure you want to delete the files for this case?
          </p>

          {selectedDeleteRow && (
            <div className="afm-delete-detail">
              <div className="afm-delete-detail-row">
                <span>Order ID</span>
                <strong>{selectedDeleteRow.orderId}</strong>
              </div>

              <div className="afm-delete-detail-row">
                <span>Case ID</span>
                <strong>{selectedDeleteRow.caseId}</strong>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="justify-content-center">
          <button
            type="button"
            className="btn btn-primary py-2 px-4"
            onClick={() => void handleDelete()}
            disabled={deletingCaseId !== null}
          >
            {deletingCaseId !== null ? "Deleting..." : "Yes, Delete"}
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary py-2 px-4"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedDeleteRow(null);
            }}
            disabled={deletingCaseId !== null}
          >
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      {/* DELETE ALL ELIGIBLE FILES MODAL */}
      <Modal
        show={showDeleteAllModal}
        onHide={() => {
          if (!deletingAll) {
            setShowDeleteAllModal(false);
          }
        }}
        dialogClassName="custom-top-modal afm-confirm-modal"
        centered
        backdrop={deletingAll ? "static" : true}
        keyboard={!deletingAll}
      >
        <Modal.Header closeButton={!deletingAll}>
          <Modal.Title className="text-danger">
            <i className="bi bi-exclamation-triangle-fill me-2" />
            Confirm Delete All
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-2">
            Are you sure you want to permanently delete all eligible case
            files?
          </p>

          <div className="afm-delete-detail">
            <div className="afm-delete-detail-row">
              <span>Eligible cases</span>
              <strong>{totalEligibleCases}</strong>
            </div>

            <div className="afm-delete-detail-row">
              <span>Eligible files</span>
              <strong>{totalEligibleFiles}</strong>
            </div>
          </div>

          <p className="text-danger small mt-3 mb-0">
            <i className="bi bi-exclamation-circle me-1" />
            Only completed and paid cases should be processed. This action
            cannot be undone.
          </p>
        </Modal.Body>

        <Modal.Footer className="justify-content-end">
          <button
            type="button"
            className="btn btn-outline-secondary px-4"
            onClick={() => setShowDeleteAllModal(false)}
            disabled={deletingAll}
          >
            Cancel
          </button>

          <button
            type="button"
            className="btn btn-danger px-4"
            onClick={() => void handleDeleteAllEligible()}
            disabled={deletingAll}
          >
            {deletingAll ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Deleting...
              </>
            ) : (
              <>
                <i className="bi bi-trash3 me-2" />
                Yes, Delete All
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminFileManagement;